import React, { useEffect, useState } from "react";
import {
  Sidebar as ProSidebar,
  Menu,
  MenuItem,
  SubMenu,
} from "react-pro-sidebar";
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  Avatar,
  Drawer,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  HomeOutlined,
  MenuOutlined,
  ShoppingBagOutlined,
  CategoryOutlined,
  ReceiptLongOutlined,
  HistoryOutlined,
  WallpaperOutlined,
  ConfirmationNumberOutlined,
  CollectionsOutlined,
  PeopleOutlineOutlined,
  LocalOfferOutlined,
  ManageAccountsOutlined,
  PersonOutline,
  LogoutOutlined,
  CampaignOutlined,
} from "@mui/icons-material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";

import imgDark from "../../assets/YTC.png";
import imgLight from "../../assets/Ytc_light.png";
import hasPermission from "../../permissions/hasPermission";

const normalizeRole = (role) =>
  String(role || "")
    .trim()
    .toLowerCase();

const PremiumMenuItem = ({ title, to, icon, collapsed, onClick }) => {
  const theme = useTheme();
  const location = useLocation();
  const isDark = theme.palette.mode === "dark";

  const isActive =
    location.pathname === to ||
    (to !== "/dashboard" && location.pathname.startsWith(to));

  return (
    <MenuItem
      component={<Link to={to} />}
      active={isActive}
      onClick={onClick}
      icon={icon}
      style={{
        borderRadius: "8px",
        color: isActive
          ? isDark
            ? "#D8C2A8"
            : "#806B52"
          : isDark
            ? "#B8B2AA"
            : "#5E5953",
        transition: "all .25s ease",
      }}
    >
      {!collapsed && (
        <Typography
          sx={{
            fontSize: "13.5px",
            fontWeight: isActive ? 600 : 450,
            letterSpacing: "0.1px",
          }}
        >
          {title}
        </Typography>
      )}
    </MenuItem>
  );
};

const SidebarContent = ({ isCollapsed, setIsCollapsed, onMenuClick }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const [openProducts, setOpenProducts] = useState(false);
  const [openOrders, setOpenOrders] = useState(false);
  const [openHome, setOpenHome] = useState(false);
  const [openTickets, setOpenTickets] = useState(false);

  // Logged-in admin information is stored after successful login.
  const [name, setName] = useState("");
  const [role, setRole] = useState("");

  useEffect(() => {
    const storedName = sessionStorage.getItem("name") || "";
    const storedRole = sessionStorage.getItem("role") || "";
    setName(storedName);
    setRole(storedRole);
  }, []);

  // Clear the current admin session and return to login.
  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (
      location.pathname.includes("/product") ||
      location.pathname.includes("/category")
    ) {
      setOpenProducts(true);
    }

    if (location.pathname.includes("/order")) {
      setOpenOrders(true);
    }

    if (location.pathname.includes("/managehome")) {
      setOpenHome(true);
    }

    if (location.pathname.includes("/ticket")) {
      setOpenTickets(true);
    }
  }, [location.pathname]);

  const background = isDark ? "#151412" : "#FAF8F5";

  const borderColor = isDark ? "rgba(255,255,255,0.07)" : "rgba(50,40,30,0.08)";

  const primaryText = isDark ? "#F3EFE9" : "#27231F";

  const secondaryText = isDark ? "#AAA49C" : "#77716A";

  const accent = isDark ? "#D1B99A" : "#806B52";

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        "& .ps-sidebar-container": {
          background: `${background} !important`,
          borderRight: `1px solid ${borderColor}`,
          height: "100vh",
          overflowX: "hidden",
        },
        "& .ps-menu-root": {
          paddingTop: "8px",
        },
        "& .ps-menu-button": {
          minHeight: "46px",
          background: "transparent !important",
          borderRadius: "8px",
          transition: "all .25s ease",
        },
        "& .ps-menu-button:hover": {
          background: isDark
            ? "rgba(255,255,255,0.045) !important"
            : "rgba(90,75,60,0.055) !important",
        },
        "& .ps-menu-button.ps-active": {
          background: isDark
            ? "rgba(209,185,154,0.12) !important"
            : "rgba(128,107,82,0.10) !important",
        },
        "& .ps-menu-icon": {
          minWidth: "34px !important",
          width: "34px !important",
        },
        "& .ps-submenu-content": {
          background: "transparent !important",
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed} width="270px" collapsedWidth="76px">
        <Menu>
          <MenuItem
            onClick={() => !isMobile && setIsCollapsed(!isCollapsed)}
            icon={
              isCollapsed ? (
                <MenuOutlined
                  sx={{
                    color: primaryText,
                    fontSize: 21,
                  }}
                />
              ) : undefined
            }
            style={{
              height: "76px",
              margin: 0,
              borderRadius: 0,
            }}
          >
            {!isCollapsed && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  px: 1,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Georgia, 'Times New Roman', serif",
                      fontSize: "19px",
                      fontWeight: 600,
                      letterSpacing: "-0.3px",
                      color: primaryText,
                    }}
                  >
                    Hindustan
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "9px",
                      letterSpacing: "3px",
                      textTransform: "uppercase",
                      color: accent,
                      mt: "-1px",
                    }}
                  >
                    Wardrobe
                  </Typography>
                </Box>

                {!isMobile && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCollapsed(true);
                    }}
                    size="small"
                  >
                    <MenuOutlined
                      sx={{
                        fontSize: 20,
                        color: secondaryText,
                      }}
                    />
                  </IconButton>
                )}

                {isMobile && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onMenuClick();
                    }}
                    size="small"
                  >
                    <MenuOutlined
                      sx={{
                        fontSize: 20,
                        color: secondaryText,
                      }}
                    />
                  </IconButton>
                )}
              </Box>
            )}
          </MenuItem>
          {/* -------------------------------------------------------------
              ADMIN PROFILE / ACCOUNT AREA
              ------------------------------------------------------------- */}
          {!isCollapsed && (
            <>
              <Box sx={{ px: 2.5, pt: 2.5, pb: 2.25 }}>
                {/* Admin identity */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Avatar
                    src={isDark ? imgDark : imgLight}
                    sx={{
                      width: 46,
                      height: 46,
                      border: `1px solid ${borderColor}`,
                      backgroundColor: isDark ? "#25221F" : "#EEE9E2",
                    }}
                  />

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: primaryText,
                      }}
                    >
                      {name || "Administrator"}
                    </Typography>

                    <Typography
                      noWrap
                      sx={{
                        fontSize: 10.5,
                        color: secondaryText,
                        mt: 0.35,
                        textTransform: "capitalize",
                        letterSpacing: "0.2px",
                      }}
                    >
                      {role || "Administrator"}
                    </Typography>
                  </Box>
                </Box>

                {/* Profile*/}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box
                    component={Link}
                    to="/profile"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 1.25,
                      py: 1,
                      borderRadius: "9px",
                      color: secondaryText,
                      textDecoration: "none",
                      transition: "all .2s ease",
                      "&:hover": {
                        color: primaryText,
                        backgroundColor: isDark
                          ? "rgba(255,255,255,0.045)"
                          : "rgba(90,75,60,0.055)",
                      },
                    }}
                  >
                    <PersonOutline sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 500 }}>
                      My Profile
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ borderColor, mx: 2 }} />
            </>
          )}

          {/* Compact account actions when the desktop sidebar is collapsed */}
          {isCollapsed && !isMobile && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                pt: 2,
              }}
            >
              <IconButton
                component={Link}
                to="/profile"
                title="My Profile"
                sx={{
                  width: 42,
                  height: 42,
                  border: `1px solid ${borderColor}`,
                  borderRadius: "10px",
                  color: secondaryText,
                }}
              >
                <PersonOutline fontSize="small" />
              </IconButton>

             
            </Box>
          )}
          {/* Dashboard Section */}

          {!isCollapsed && (
            <Typography
              sx={{
                px: 3,
                pt: 3,
                pb: 1,
                fontSize: 9,
                letterSpacing: "2px",
                textTransform: "uppercase",
                fontWeight: 700,
                color: accent,
              }}
            >
              Overview
            </Typography>
          )}
          {hasPermission("Dashboard") && (
            <PremiumMenuItem
              title="Dashboard"
              to="/dashboard"
              collapsed={isCollapsed}
              onClick={onMenuClick}
              icon={
                <HomeOutlined
                  sx={{
                    fontSize: 20,
                    color:
                      location.pathname === "/dashboard"
                        ? accent
                        : secondaryText,
                  }}
                />
              }
            />
          )}

          {/* Catlog Section */}
          {hasPermission("ViewProduct") && (
            <>
              {!isCollapsed && (
                <Typography
                  sx={{
                    px: 3,
                    pt: 3,
                    pb: 1,
                    fontSize: 9,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    color: accent,
                  }}
                >
                  Catalog
                </Typography>
              )}

              <SubMenu
                label="Product Management"
                icon={
                  <ShoppingBagOutlined
                    sx={{
                      fontSize: 20,
                      color: secondaryText,
                    }}
                  />
                }
                open={openProducts}
                onClick={() => setOpenProducts(!openProducts)}
              >
                <PremiumMenuItem
                  title="Products"
                  to="/product"
                  collapsed={isCollapsed}
                  onClick={onMenuClick}
                  icon={
                    <ShoppingBagOutlined
                      sx={{
                        fontSize: 18,
                      }}
                    />
                  }
                />

                <PremiumMenuItem
                  title="Categories"
                  to="/category"
                  collapsed={isCollapsed}
                  onClick={onMenuClick}
                  icon={
                    <CategoryOutlined
                      sx={{
                        fontSize: 18,
                      }}
                    />
                  }
                />
              </SubMenu>
            </>
          )}

          {hasPermission("CollectionProducts") && (
            <PremiumMenuItem
              key="collections"
              title="Collections"
              to="/collections"
              collapsed={isCollapsed}
              onClick={onMenuClick}
              icon={
                <CollectionsOutlined
                  sx={{
                    fontSize: 20,
                    color: secondaryText,
                  }}
                />
              }
            />
          )}

          {hasPermission("SponserdProduct") && (
            <PremiumMenuItem
              key="sponsered-products"
              title="Sponsered Products"
              to="/sponsered-products"
              collapsed={isCollapsed}
              onClick={onMenuClick}
                    // icon: <CampaignOutlinedIcon />,
              
              icon={
                <CampaignOutlinedIcon
                  sx={{
                    fontSize: 20,
                    color: secondaryText,
                  }}
                />
              }
            />
          )}

           {hasPermission("TopProduct") && (
            <PremiumMenuItem
              key="top-products"
              title="Top Products"
              to="/top-products"
              collapsed={isCollapsed}
              onClick={onMenuClick}
              
              icon={
                <StarOutlineRoundedIcon
                  sx={{
                    fontSize: 20,
                    color: secondaryText,
                  }}
                />
              }
            />
          )}

          {/* Customer Section */}
          {hasPermission("UserTickets") && (
            <>
              {!isCollapsed && (
                <Typography
                  sx={{
                    px: 3,
                    pt: 3,
                    pb: 1,
                    fontSize: 9,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    color: accent,
                  }}
                >
                  Customer
                </Typography>
              )}

              <SubMenu
                label="Support"
                icon={
                  <ConfirmationNumberOutlined
                    sx={{
                      fontSize: 20,
                      color: secondaryText,
                    }}
                  />
                }
                open={openTickets}
                onClick={() => setOpenTickets(!openTickets)}
              >
                <PremiumMenuItem
                  title="User Tickets"
                  to="/ticket"
                  collapsed={isCollapsed}
                  onClick={onMenuClick}
                  icon={
                    <ConfirmationNumberOutlined
                      sx={{
                        fontSize: 18,
                      }}
                    />
                  }
                />
              </SubMenu>
            </>
          )}

          {hasPermission("Users") && (
            <PremiumMenuItem
              title="Customers"
              to="/user-management"
              collapsed={isCollapsed}
              onClick={onMenuClick}
              icon={
                <PeopleOutlineOutlined
                  sx={{
                    fontSize: 20,
                    color: secondaryText,
                  }}
                />
              }
            />
          )}

          {/* Operations Section */}
          {hasPermission("OrderHistory") && (
            <>
              {!isCollapsed && (
                <Typography
                  sx={{
                    px: 3,
                    pt: 3,
                    pb: 1,
                    fontSize: 9,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    color: accent,
                  }}
                >
                  Operations
                </Typography>
              )}

              <SubMenu
                label="Order Management"
                icon={
                  <ReceiptLongOutlined
                    sx={{
                      fontSize: 20,
                      color: secondaryText,
                    }}
                  />
                }
                open={openOrders}
                onClick={() => setOpenOrders(!openOrders)}
              >
                <PremiumMenuItem
                  title="Order History"
                  to="/order-history"
                  collapsed={isCollapsed}
                  onClick={onMenuClick}
                  icon={
                    <HistoryOutlined
                      sx={{
                        fontSize: 18,
                      }}
                    />
                  }
                />
              </SubMenu>
            </>
          )}

          {hasPermission("HomePageMangement") && (
            <SubMenu
              label="Home Management"
              icon={
                <WallpaperOutlined
                  sx={{
                    fontSize: 20,
                    color: secondaryText,
                  }}
                />
              }
              open={openHome}
              onClick={() => setOpenHome(!openHome)}
            >
              <PremiumMenuItem
                title="Home Banners"
                to="/managehome"
                collapsed={isCollapsed}
                onClick={onMenuClick}
                icon={
                  <WallpaperOutlined
                    sx={{
                      fontSize: 18,
                    }}
                  />
                }
              />
            </SubMenu>
          )}

          {/* Marketing Section */}
          {hasPermission("MarketingAnalytics") && (
            <>
              {!isCollapsed && (
                <Typography
                  sx={{
                    px: 3,
                    pt: 3,
                    pb: 1,
                    fontSize: 9,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    color: accent,
                  }}
                >
                  Marketing
                </Typography>
              )}

              <PremiumMenuItem
                title="Marketing Analytics"
                to="/marketing-analytics"
                collapsed={isCollapsed}
                onClick={onMenuClick}
                icon={
                  <LocalOfferOutlined
                    sx={{
                      fontSize: 20,
                      color: secondaryText,
                    }}
                  />
                }
              />
            </>
          )}
          {/* Administration Section */}
          {hasPermission("AdminUser") && (
            <>
              {!isCollapsed && (
                <Typography
                  sx={{
                    px: 3,
                    pt: 3,
                    pb: 1,
                    fontSize: 9,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    color: accent,
                  }}
                >
                  Administration
                </Typography>
              )}

              <PremiumMenuItem
                title="Admin Management"
                to="/admin-management"
                collapsed={isCollapsed}
                onClick={onMenuClick}
                icon={
                  <ManageAccountsOutlined
                    sx={{
                      fontSize: 20,
                      color: secondaryText,
                    }}
                  />
                }
              />
            </>
          )}
          {isCollapsed && !isMobile && (
            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <IconButton
                onClick={() => setIsCollapsed(false)}
                sx={{
                  width: 42,
                  height: 42,
                  border: `1px solid ${borderColor}`,
                  color: secondaryText,
                }}
              >
                <MenuOutlined fontSize="small" />
              </IconButton>
            </Box>
          )}

          {/* ================================================================
    BOTTOM ACTIONS
    Logout always stays below all menu options
================================================================ */}

          <Box
            sx={{
              mt: 2,
              pt: 1.5,
              pb: 1.5,
              px: isCollapsed ? 1 : 2,
              borderTop: `1px solid ${borderColor}`,
            }}
          >
            <MenuItem
              onClick={handleLogout}
              icon={
                <LogoutOutlined
                  sx={{
                    fontSize: 20,
                    color: isDark ? "#D88A8A" : "#A94442",
                  }}
                />
              }
              style={{
                borderRadius: "8px",
                color: isDark ? "#D88A8A" : "#A94442",
                minHeight: "46px",
                transition: "all .2s ease",
              }}
            >
              {!isCollapsed && (
                <Typography
                  sx={{
                    fontSize: "13.5px",
                    fontWeight: 500,
                  }}
                >
                  Logout
                </Typography>
              )}
            </MenuItem>
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

const MySidebar = ({ children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isCollapsed, setIsCollapsed] = useState(false);

  const [mobileOpen, setMobileOpen] = useState(false);

  const background = isDark ? "#11100F" : "#F6F3EF";

  const borderColor = isDark ? "rgba(255,255,255,0.07)" : "rgba(50,40,30,0.08)";

  const handleMenuClick = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  if (isMobile) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: background,
        }}
      >
        <IconButton
          onClick={() => setMobileOpen(true)}
          sx={{
            position: "fixed",
            top: 14,
            left: 14,
            zIndex: 1300,
            width: 42,
            height: 42,
            border: `1px solid ${borderColor}`,
            backgroundColor: background,
            color: isDark ? "#F3EFE9" : "#27231F",
            boxShadow: "0 2px 10px rgba(0,0,0,.08)",
            "&:hover": {
              backgroundColor: background,
            },
          }}
        >
          <MenuOutlined />
        </IconButton>

        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: 270,
              backgroundColor: isDark ? "#151412" : "#FAF8F5",
              borderRight: `1px solid ${borderColor}`,
            },
          }}
        >
          <SidebarContent
            isCollapsed={false}
            setIsCollapsed={() => { }}
            onMenuClick={handleMenuClick}
          />
        </Drawer>

        <Box
          component="main"
          sx={{
            // width: "100%",
            // minHeight: "100vh",
            // backgroundColor: background,
            flex: 1,
            width: "100%",
            overflowY: "auto",
            backgroundColor: background,
            pt: "60px",
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: background,
      }}
    >
      <Box
        sx={{
          width: isCollapsed ? "76px" : "270px",
          flexShrink: 0,
          height: "100vh",
          transition: "width .25s ease",
        }}
      >
        <SidebarContent
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          onMenuClick={handleMenuClick}
        />
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: background,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "70px",
            borderBottom: `1px solid ${borderColor}`,
            display: "flex",
            alignItems: "center",
            px: 3,
            backgroundColor: background,
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: isDark ? "#F3EFE9" : "#27231F",
            }}
          >
            Hindustan Wardrobe
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            width: "100%",
            minHeight: "calc(100vh - 70px)",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default MySidebar;
